import { createContext, useContext, useMemo, type ReactNode } from "react";

/** Canonical bulk-create route. Screens fall back to this when no provider is mounted. */
export const BULK_CREATE_COMPANY_BASE_PATH = "/onboarding/bulk-create-company";

/** Historical redesign alias retained only for redirects to the canonical route. */
export const BULK_CREATE_REDESIGN_BASE_PATH = "/onboarding/bulk-create-redesign";

export type BulkCreatePresentation = "table" | "cards";

export interface BulkCreateFlow {
  basePath: string;
  uploadPath: string;
  reviewPath: string;
  resultsPath: string;
  presentation: BulkCreatePresentation;
  title: string;
}

const DEFAULT_FLOW: BulkCreateFlow = {
  basePath: BULK_CREATE_COMPANY_BASE_PATH,
  uploadPath: BULK_CREATE_COMPANY_BASE_PATH,
  reviewPath: `${BULK_CREATE_COMPANY_BASE_PATH}/review`,
  resultsPath: `${BULK_CREATE_COMPANY_BASE_PATH}/results`,
  presentation: "table",
  title: "Bulk create companies",
};

const BulkCreateFlowContext = createContext<BulkCreateFlow>(DEFAULT_FLOW);

interface BulkCreateFlowProviderProps {
  basePath: string;
  presentation: BulkCreatePresentation;
  title: string;
  children: ReactNode;
}

/** Overrides wizard routes and page title for tests or an alternate mount point. */
export function BulkCreateFlowProvider({
  basePath,
  presentation,
  title,
  children,
}: BulkCreateFlowProviderProps) {
  const value = useMemo<BulkCreateFlow>(
    () => ({
      basePath,
      uploadPath: basePath,
      reviewPath: `${basePath}/review`,
      resultsPath: `${basePath}/results`,
      presentation,
      title,
    }),
    [basePath, presentation, title]
  );

  return (
    <BulkCreateFlowContext.Provider value={value}>
      {children}
    </BulkCreateFlowContext.Provider>
  );
}

/** Route targets and preview style for the bulk-create wizard currently mounted. */
// eslint-disable-next-line react-refresh/only-export-components
export function useBulkCreateFlow(): BulkCreateFlow {
  return useContext(BulkCreateFlowContext);
}
